import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { BrokerReviewListModel, ManagementEntrySummarySearchModel } from '@data-layer/models/management';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { BondModel, PaginatedResultModel } from '@data-layer/models/common';

@Injectable({
  providedIn: 'root',
})
export class EntrySummaryCustomsBrokerManagementRepository {
  constructor(private apiService: ApiService) {}

  public getBrokerReviewPending(
    params: ManagementEntrySummarySearchModel,
    isShowLoader: boolean = true,
  ): Observable<PaginatedResultModel<BrokerReviewListModel>> {
    // The endpoint is server-scoped to BrokerReviewPending — strip any state filter so a
    // stale `state`/`states` (e.g. restored from persisted search) never reaches the query.
    const { state, states, ...safeParams }: ManagementEntrySummarySearchModel = params;
    return this.apiService.get<PaginatedResultModel<BrokerReviewListModel>>(
      `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries`,
      { params: safeParams },
      { isShowLoader },
    );
  }

  public getById(id: number, isShowLoader: boolean = true): Observable<EntrySummaryModel> {
    return this.apiService.get<EntrySummaryModel>(
      `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/${id}`,
      null,
      { isShowLoader },
    );
  }

  public update(id: number, entrySummary: Partial<EntrySummaryModel>): Observable<EntrySummaryModel> {
    return this.apiService.put<EntrySummaryModel>(
      `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/${id}`,
      entrySummary,
    );
  }

  public batchConfirmReview(referenceNumbers: string[]): Observable<never> {
    return this.apiService.post(
      `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/batch-confirm-review`,
      referenceNumbers,
      null,
      { isShowLoader: false },
    );
  }

  public updateBond(id: number, bond: BondModel): Observable<never> {
    return this.apiService.put(`${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/${id}/bond`, bond);
  }

  public connectBondRequest(id: number, bondRequestId: number): Observable<never> {
    return this.apiService.put(
      `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/${id}/bond-request-id`,
      bondRequestId,
    );
  }

  public disconnectBondRequest(id: number): Observable<never> {
    return this.apiService.delete(`${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/${id}/bond-request-id`);
  }

  /** Approve the broker review of a single ES → sends it to Shell (status 120). Page manages its own overlay → no global loader. */
  public approveBrokerReview(id: number): Observable<never> {
    return this.apiService.post(
      `${environment.url.prefixCustoms}v2/management/customs-broker/entry-summaries/${id}/broker-review/approve`,
      null,
      null,
      { isShowLoader: false },
    );
  }
}
